import React, { useMemo, Suspense } from 'react';
import { AnalysisResult } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';


// Lazy load the Lanyard component for better performance
const Lanyard = React.lazy(() => import('../blocks/Components/Lanyard/Lanyard'));

interface OptimizationTabProps {
  analysisResults: AnalysisResult[];
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const OptimizationTab: React.FC<OptimizationTabProps> = ({ analysisResults, showStatus }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  
  // 生成优化建议数据
  const optimizationData = useMemo(() => {
    if (analysisResults.length === 0) return null;

    const best = analysisResults[0]; // 最优方案
    
    // 计算采购清单
    const motorCostEach = Math.round((parseFloat(best.totalCost.toString()) - 300) * 0.3); // 30%为电机成本
    const batteryCost = Math.round((parseFloat(best.totalCost.toString()) - 300) * 0.25); // 25%为电池成本
    const propellerCostEach = Math.round((parseFloat(best.totalCost.toString()) - 300) * 0.075); // 7.5%为单个桨叶成本
    const escCost = 80; // ESC成本固定

    return {
      best,
      purchaseList: [
        {
          type: t('optimization.purchase.motor'),
          spec: best.motor,
          quantity: 2,
          unitPrice: motorCostEach,
          subtotal: motorCostEach * 2,
          notes: t('optimization.purchase.motorNotes')
        },
        {
          type: t('optimization.purchase.battery'),
          spec: best.battery,
          quantity: 1,
          unitPrice: batteryCost,
          subtotal: batteryCost,
          notes: t('optimization.purchase.batteryNotes')
        },
        {
          type: t('optimization.purchase.propeller'),
          spec: best.propeller,
          quantity: 4,
          unitPrice: propellerCostEach,
          subtotal: propellerCostEach * 4,
          notes: t('optimization.purchase.propellerNotes')
        },
        {
          type: t('optimization.purchase.esc'),
          spec: `${best.escSpec} × 2`,
          quantity: 2,
          unitPrice: escCost,
          subtotal: escCost * 2,
          notes: t('optimization.purchase.escNotes')
        }
      ]
    };
  }, [analysisResults, t]);

  // 生成续航分析文本
  const getEnduranceAnalysis = (endurance: number) => {
    if (endurance >= 30) {
      return t('optimization.analysis.endurance.excellent');
    } else if (endurance >= 25) {
      return t('optimization.analysis.endurance.good');
    } else if (endurance >= 18) {
      return t('optimization.analysis.endurance.minimum');
    } else {
      return t('optimization.analysis.endurance.belowExpected');
    }
  };

  // 生成推力分析文本
  const getThrustAnalysis = (thrustMargin: number) => {
    if (thrustMargin >= 100) {
      return t('optimization.analysis.thrust.excellent');
    } else if (thrustMargin >= 80) {
      return t('optimization.analysis.thrust.good');
    } else if (thrustMargin >= 50) {
      return t('optimization.analysis.thrust.moderate');
    } else {
      return t('optimization.analysis.thrust.low');
    }
  };

  if (!optimizationData) {
    return (
      <div className="space-y-8">
        <div>
          <h2 
            className="text-lg font-semibold mb-6 pb-2 border-b"
            style={{ 
              color: colors.text,
              borderColor: colors.border 
            }}
          >
            {t('optimization.title')}
          </h2>
          <div className="text-center py-12">
            <p style={{ color: colors.textMuted }}>
              {t('optimization.noData')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { best, purchaseList } = optimizationData;
  const totalPurchaseCost = purchaseList.reduce((sum, item) => sum + item.subtotal, 0);

  return (
    <div className="space-y-8 relative">
      <div className="relative z-10">
        <h2 
          className="text-lg font-semibold mb-6 pb-2 border-b"
          style={{ 
            color: colors.text,
            borderColor: colors.border 
          }}
        >
          {t('optimization.title')}
        </h2>

        {/* Hero区域 - 推荐方案主展示 */}
        <div className="mb-8">
          <div 
            className="rounded-lg border p-8"
            style={{ 
              backgroundColor: colors.surface,
              borderColor: colors.border 
            }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
                  {t('optimization.recommendedConfig')}
                </h2>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {t('optimization.recommendedConfigDesc')}
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold" style={{ color: colors.accent }}>
                  ¥{best.totalCost}
                </div>
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  {t('optimization.estimatedCost')}
                </div>
              </div>
            </div>

            {/* 主要配置展示 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* 核心组件 */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div 
                    className="text-center p-4 rounded-lg border" 
                    style={{ 
                      backgroundColor: `${colors.background}40`,
                      borderColor: colors.borderLight 
                    }}
                  >
                    <div className="text-sm mb-1" style={{ color: colors.textSecondary }}>
                      {t('optimization.motor')}
                    </div>
                    <div className="font-semibold" style={{ color: colors.text }}>
                      {best.motor}
                    </div>
                  </div>
                  <div 
                    className="text-center p-4 rounded-lg border" 
                    style={{ 
                      backgroundColor: `${colors.background}40`,
                      borderColor: colors.borderLight 
                    }}
                  >
                    <div className="text-sm mb-1" style={{ color: colors.textSecondary }}>
                      {t('optimization.battery')}
                    </div>
                    <div className="font-semibold" style={{ color: colors.text }}>
                      {best.battery}
                    </div>
                  </div>
                  <div 
                    className="text-center p-4 rounded-lg border" 
                    style={{ 
                      backgroundColor: `${colors.background}40`,
                      borderColor: colors.borderLight 
                    }}
                  >
                    <div className="text-sm mb-1" style={{ color: colors.textSecondary }}>
                      {t('optimization.propeller')}
                    </div>
                    <div className="font-semibold" style={{ color: colors.text }}>
                      {best.propeller}
                    </div>
                  </div>
                </div>

                {/* 关键指标 */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold mb-1" style={{ color: colors.success }}>
                      {best.endurance}
                    </div>
                    <div className="text-xs" style={{ color: colors.textSecondary }}>
                      {t('optimization.endurance')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold mb-1" style={{ color: colors.accent }}>
                      {best.totalThrust}g
                    </div>
                    <div className="text-xs" style={{ color: colors.textSecondary }}>
                      {t('optimization.maxThrust')}
                    </div>
                  </div>
                  <div className="text-2xl font-bold mb-1 text-center" style={{ color: colors.text }}>
                    <div>
                      {(parseFloat(best.thrustMargin) / 100 + 1).toFixed(1)}:1
                    </div>
                    <div className="text-xs" style={{ color: colors.textSecondary }}>
                      {t('optimization.thrustWeightRatio')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold mb-1" style={{ color: colors.text }}>
                      {best.totalWeight}g
                    </div>
                    <div className="text-xs" style={{ color: colors.textSecondary }}>
                      {t('optimization.systemWeight')}
                    </div>
                  </div>
                </div>
              </div>

              {/* 性能评估 */}
              <div>
                <h4 className="text-sm font-semibold mb-4" style={{ color: colors.text }}>
                  {t('optimization.performanceEvaluation')}
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: colors.textSecondary }}>{t('optimization.endurancePerformance')}</span>
                      <span className="text-sm font-medium" style={{ color: parseFloat(best.endurance.toString()) >= 25 ? colors.success : colors.warning }}>
                        {parseFloat(best.endurance.toString()) >= 25 ? t('optimization.excellent') : t('optimization.good')}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full" 
                        style={{ 
                          width: `${Math.min(parseFloat(best.endurance.toString()) / 30 * 100, 100)}%`,
                          backgroundColor: parseFloat(best.endurance.toString()) >= 25 ? colors.success : colors.warning 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: colors.textSecondary }}>{t('optimization.thrustMargin')}</span>
                      <span className="text-sm font-medium" style={{ color: parseFloat(best.thrustMargin.toString()) >= 80 ? colors.success : colors.warning }}>
                        {parseFloat(best.thrustMargin.toString()) >= 80 ? t('optimization.sufficient') : t('optimization.moderate')}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full" 
                        style={{ 
                          width: `${Math.min(parseFloat(best.thrustMargin.toString()), 100)}%`,
                          backgroundColor: parseFloat(best.thrustMargin.toString()) >= 80 ? colors.success : colors.warning 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: colors.textSecondary }}>{t('optimization.costControl')}</span>
                      <span className="text-sm font-medium" style={{ color: parseFloat(best.totalCost.toString()) <= 2000 ? colors.success : colors.accent }}>
                        {parseFloat(best.totalCost.toString()) <= 2000 ? t('optimization.economical') : t('optimization.reasonable')}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full" 
                        style={{ 
                          width: `${Math.max(100 - parseFloat(best.totalCost.toString()) / 3000 * 100, 20)}%`,
                          backgroundColor: parseFloat(best.totalCost.toString()) <= 2000 ? colors.success : colors.accent 
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 详细信息区域 */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* 技术分析 - 占2列 */}
          <div className="lg:col-span-2">
            <div 
              className="rounded-lg border p-6 h-full"
              style={{ 
                backgroundColor: colors.background,
                borderColor: colors.borderLight 
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
                {t('optimization.technicalAnalysis')}
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                    <span className="font-medium" style={{ color: colors.text }}>{t('optimization.endurancePerformance')}：</span>
                    {getEnduranceAnalysis(parseFloat(best.endurance))}
                  </p>
                </div>
                <div>
                  <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                    <span className="font-medium" style={{ color: colors.text }}>{t('optimization.thrustConfig')}：</span>
                    {getThrustAnalysis(parseFloat(best.thrustMargin))}
                  </p>
                </div>
                <div className="pt-4 border-t" style={{ borderColor: colors.borderLight }}>
                  <h4 className="text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    系统建议
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: colors.textSecondary }}>
                    <li>• ESC规格：{best.escSpec}电调，确保电流裕量</li>
                    <li>• 重量控制：系统总重{best.totalWeight}g，注意平衡</li>
                    <li>• 成本预算：动力系统约¥{best.totalCost}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 采购清单 - 占1列 */}
          <div>
            <div 
              className="rounded-lg border p-6"
              style={{ 
                backgroundColor: colors.background,
                borderColor: colors.borderLight 
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
                {t('optimization.purchaseList')}
              </h3>
              <div className="space-y-3">
                {purchaseList.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium" style={{ color: colors.text }}>
                        {item.type}
                      </div>
                      <div className="text-xs" style={{ color: colors.textSecondary }}>
                        × {item.quantity}
                      </div>
                    </div>
                    <div className="text-sm font-semibold" style={{ color: colors.text }}>
                      ¥{item.subtotal}
                    </div>
                  </div>
                ))}
                <div 
                  className="flex justify-between items-center pt-3 border-t"
                  style={{ borderColor: colors.borderLight }}
                >
                  <span className="font-semibold" style={{ color: colors.text }}>
                    {t('optimization.total')}
                  </span>
                  <span className="text-lg font-bold" style={{ color: colors.accent }}>
                    ¥{totalPurchaseCost}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 实施指导 - Linear式简洁布局 */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-base font-semibold mb-4" style={{ color: colors.text }}>
              实施要点
            </h3>
            <div className="space-y-2">
              {[
                "优先采购电机和ESC进行地面测试",
                "注意电机旋转方向，确保共轴配置正确",
                "首次飞行前进行充分地面测试验证",
                "准备备用电池和桨叶以应对意外"
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 py-2">
                  <div 
                    className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: colors.accent }}
                  />
                  <span className="text-sm" style={{ color: colors.textSecondary }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-4" style={{ color: colors.text }}>
              注意事项
            </h3>
            <div className="space-y-2">
              {[
                "实际续航受飞行模式和环境影响",
                "环境温度显著影响电池性能",
                "定期检查桨叶磨损情况",
                "保持电池适当充电状态"
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 py-2">
                  <div 
                    className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: colors.warning }}
                  />
                  <span className="text-sm" style={{ color: colors.textSecondary }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hanging Lanyard - Background decoration */}
      <div className="hidden lg:block absolute right-0 left-10 top-0 z-10 overflow-hidden w-full" style={{ top: '364px',height: '80%', width: '100%' }}>
        <div className="w-full h-full relative">
          <Suspense fallback={null}>
            <div className="w-full h-full">
              <Lanyard 
                position={[-6, 0, 20]}
                gravity={[0, -55, 0]}
                fov={35}
                transparent={true}
              />
            </div>
          </Suspense>
        </div>
      </div>

      {/* Hanging Lanyard - Mobile version */}
      <div className="block lg:hidden absolute right-0 left-0 top-0 z-10 overflow-hidden" style={{ top: '29px', height: '100vh', width: '91vw'}}>
        <div className="w-full h-full relative">
          <Suspense fallback={null}>
            <div className="w-full h-full">
              <Lanyard 
                position={[-6, 0, 35]}
                gravity={[0, -55, 0]}
                fov={25}
                transparent={true}
              />
            </div>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default OptimizationTab; 